import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
// AdminLayout is handled by the parent AdminAudioPage
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Upload,
  Search,
  Pencil,
  Trash2,
  EyeOff,
  Eye,
  Music,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';

const DEFAULT_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='6' fill='%23374151'/%3E%3Cpath d='M32 20a12 12 0 1 1 0 24 12 12 0 0 1 0-24zm0 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm0 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8z' fill='%236B7280'/%3E%3C/svg%3E";

// ── TrackImage ────────────────────────────────────────────────────────────────
const TrackImage = ({ src, size = 48 }) => {
  const [err, setErr] = useState(false);
  return (
    <img
      src={err || !src ? DEFAULT_IMG : src}
      alt=""
      width={size}
      height={size}
      onError={() => setErr(true)}
      className="rounded object-cover border border-border/40 bg-muted shrink-0"
      style={{ width: size, height: size }}
    />
  );
};

// ── UploadZone ────────────────────────────────────────────────────────────────
const UploadZone = ({ onFiles }) => {
  const { language } = useAuth();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      /\.(mp3|wav|ogg|flac)$/i.test(f.name),
    );
    if (files.length) onFiles(files);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={cn(
        'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
      )}
    >
      <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
      <p className="text-sm text-muted-foreground">
        {t('admin_audio_upload_hint', language)} <span className="text-primary underline">{t('admin_audio_browse', language)}</span>
      </p>
      <p className="text-xs text-muted-foreground mt-1">{t('admin_audio_upload_formats', language)}</p>
      <input
        ref={inputRef}
        type="file"
        accept=".mp3,.wav,.ogg,.flac"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(Array.from(e.target.files));
          e.target.value = '';
        }}
      />
    </div>
  );
};

// ── EditTrackDialog ────────────────────────────────────────────────────────────
const EditTrackDialog = ({ track, collections, open, onClose, onSaved }) => {
  const { language } = useAuth();
  const [episodes, setEpisodes] = useState('');
  const [tags, setTags] = useState('');
  const [colIds, setColIds] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const coverRef = useRef(null);

  useEffect(() => {
    if (track) {
      setEpisodes(track.episodes || track.title || '');
      setTags((track.tags || []).join(', '));
      setColIds((track.collections || []).join(', '));
      setIsActive(track.is_active !== false);
      setCover(null);
      setError('');
    }
  }, [track]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('episodes', episodes);
      fd.append('tags', tags);
      fd.append('collections', colIds);
      fd.append('is_active', isActive ? 'true' : 'false');
      if (cover) fd.append('cover', cover);
      await axios.patch(`/api/admin/audio/tracks/${track.id}`, fd);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin_audio_save_error', language));
    } finally {
      setSaving(false);
    }
  };

  if (!track) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin_audio_edit_track', language)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t('admin_audio_title_label', language)}</Label>
            <Input value={episodes} onChange={(e) => setEpisodes(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{t('admin_audio_tags_label', language)}</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="combat, ambient, boss" className="mt-1" />
          </div>
          <div>
            <Label>{t('admin_audio_collections_label', language)}</Label>
            <Input value={colIds} onChange={(e) => setColIds(e.target.value)} placeholder="col_xxx, col_yyy" className="mt-1" />
            {collections.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {collections.map((c) => (
                  <Badge
                    key={c.id}
                    variant="outline"
                    className="cursor-pointer text-xs"
                    onClick={() => {
                      const current = colIds.split(',').map((s) => s.trim()).filter(Boolean);
                      if (current.includes(c.id)) {
                        setColIds(current.filter((id) => id !== c.id).join(', '));
                      } else {
                        setColIds([...current, c.id].join(', '));
                      }
                    }}
                  >
                    {colIds.includes(c.id) ? '✓ ' : ''}{c.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>{t('admin_audio_active', language)}</Label>
          </div>
          <div>
            <Label>{t('admin_audio_change_cover', language)}</Label>
            <div className="flex gap-2 mt-1">
              <Button variant="outline" size="sm" onClick={() => coverRef.current?.click()}>
                {cover ? cover.name : t('admin_audio_choose_image', language)}
              </Button>
              {cover && <Button variant="ghost" size="sm" onClick={() => setCover(null)}><X className="h-4 w-4" /></Button>}
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => setCover(e.target.files?.[0] || null)} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', language)}</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? t('loading', language) : t('save', language)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── UploadDialog ───────────────────────────────────────────────────────────────
const UploadDialog = ({ files, collections, open, onClose, onUploaded }) => {
  const { language } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (files) {
      setItems(files.map((f) => ({
        file: f,
        title: f.name.replace(/\.[^.]+$/, '').replace(/_/g, ' '),
        tags: '',
        colIds: '',
        status: 'pending',
        error: '',
      })));
    }
  }, [files]);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const handleUpload = async () => {
    setUploading(true);
    let anyOk = false;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      updateItem(i, { status: 'uploading' });
      try {
        const fd = new FormData();
        fd.append('file', it.file);
        fd.append('title', it.title);
        fd.append('tags', it.tags);
        fd.append('collections', it.colIds);
        await axios.post('/api/admin/audio/tracks', fd);
        updateItem(i, { status: 'done' });
        anyOk = true;
      } catch (err) {
        updateItem(i, { status: 'error', error: err.response?.data?.detail || 'Erreur' });
      }
    }
    setUploading(false);
    if (anyOk) onUploaded();
  };

  const allDone = items.every((it) => it.status === 'done');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !uploading && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin_audio_import_title', language)} ({items.length})</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 pr-2">
            {items.map((it, idx) => (
              <div key={idx} className="border border-border/60 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{it.file.name}</span>
                  <Badge
                    variant={it.status === 'done' ? 'default' : it.status === 'error' ? 'destructive' : 'outline'}
                    className="ml-2 shrink-0"
                  >
                    {it.status === 'pending' ? t('admin_audio_pending', language) : it.status === 'uploading' ? t('admin_audio_uploading', language) : it.status === 'done' ? t('admin_audio_done', language) : 'Erreur'}
                  </Badge>
                </div>
                {it.error && <p className="text-xs text-destructive">{it.error}</p>}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t('admin_audio_title_label', language)}</Label>
                    <Input
                      value={it.title}
                      onChange={(e) => updateItem(idx, { title: e.target.value })}
                      className="mt-0.5 h-8 text-xs"
                      disabled={it.status !== 'pending'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('admin_audio_tags_label', language)}</Label>
                    <Input
                      value={it.tags}
                      onChange={(e) => updateItem(idx, { tags: e.target.value })}
                      placeholder="combat, ambient"
                      className="mt-0.5 h-8 text-xs"
                      disabled={it.status !== 'pending'}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t('admin_audio_collections', language)}</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {collections.map((c) => (
                      <Badge
                        key={c.id}
                        variant={it.colIds.includes(c.id) ? 'default' : 'outline'}
                        className="cursor-pointer text-xs"
                        onClick={() => {
                          if (it.status !== 'pending') return;
                          const current = it.colIds.split(',').map((s) => s.trim()).filter(Boolean);
                          if (current.includes(c.id)) {
                            updateItem(idx, { colIds: current.filter((id) => id !== c.id).join(', ') });
                          } else {
                            updateItem(idx, { colIds: [...current, c.id].join(', ') });
                          }
                        }}
                      >
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            {allDone ? t('close', language) : t('cancel', language)}
          </Button>
          {!allDone && (
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? t('admin_audio_uploading', language) : t('admin_audio_import_title', language)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const AdminAudioLibraryPage = () => {
  const { language } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [editTrack, setEditTrack] = useState(null);
  const [deleteTrack, setDeleteTrack] = useState(null);
  const [permanent, setPermanent] = useState(false);
  const [uploadFiles, setUploadFiles] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50, search, tags: tagFilter };
      if (activeFilter) params.is_active = activeFilter;
      const [tracksRes, colsRes] = await Promise.all([
        axios.get('/api/admin/audio/tracks', { params }),
        axios.get('/api/admin/audio/collections'),
      ]);
      setTracks(tracksRes.data.tracks);
      setTotal(tracksRes.data.total);
      setPages(tracksRes.data.pages);
      setCollections(colsRes.data.collections || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, tagFilter, activeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteTrack) return;
    try {
      await axios.delete(`/api/admin/audio/tracks/${deleteTrack.id}?permanent=${permanent}`);
      setDeleteTrack(null);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const collectionName = (id) => collections.find((c) => c.id === id)?.name || id;

  return (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-cinzel flex items-center gap-2">
              <Music className="h-6 w-6" />
              {t('admin_audio_library', language)}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{total} {t('admin_audio_total_tracks', language)}</p>
          </div>
        </div>

        {/* Zone d'upload */}
        <UploadZone onFiles={(files) => setUploadFiles(files)} />

        {/* Filtres */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('search', language)}
              className="pl-8"
            />
          </div>
          <Input
            value={tagFilter}
            onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
            placeholder={t('admin_audio_filter_tag', language)}
            className="w-40"
          />
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
            className="border border-input rounded-md px-2 py-1.5 text-sm bg-background"
          >
            <option value="">{t('admin_audio_all_statuses', language)}</option>
            <option value="true">{t('admin_audio_active_only', language)}</option>
            <option value="false">{t('admin_audio_disabled_only', language)}</option>
          </select>
        </div>

        {/* Liste des tracks */}
        {loading ? (
          <div className="text-muted-foreground animate-pulse">{t('loading', language)}</div>
        ) : (
          <div className="space-y-2">
            {tracks.map((track) => (
              <Card key={track.id} className={cn(!track.is_active && 'opacity-50')}>
                <CardContent className="flex items-center gap-3 p-3">
                  <TrackImage src={track.pic} size={48} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{track.episodes || track.title || track.id}</p>
                    <p className="text-xs text-muted-foreground truncate">{track.filename || track.id}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(track.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">{tag}</Badge>
                      ))}
                      {(track.collections || []).map((cid) => (
                        <Badge key={cid} variant="outline" className="text-xs px-1.5 py-0">{collectionName(cid)}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!track.is_active && <Badge variant="destructive" className="text-xs">{t('admin_audio_inactive', language)}</Badge>}
                    <Button variant="ghost" size="icon" onClick={() => setEditTrack(track)} title={t('admin_audio_edit_track', language)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setDeleteTrack(track); setPermanent(false); }}
                      title={t('delete', language)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {tracks.length === 0 && (
              <p className="text-center text-muted-foreground py-8">{t('admin_audio_no_tracks', language)}</p>
            )}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

      {/* Dialogs */}
      <EditTrackDialog
        track={editTrack}
        collections={collections}
        open={!!editTrack}
        onClose={() => setEditTrack(null)}
        onSaved={fetchData}
      />

      <UploadDialog
        files={uploadFiles}
        collections={collections}
        open={!!uploadFiles}
        onClose={() => setUploadFiles(null)}
        onUploaded={() => { setUploadFiles(null); fetchData(); }}
      />

      {/* Confirm delete */}
      <AlertDialog open={!!deleteTrack} onOpenChange={(v) => !v && setDeleteTrack(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin_audio_delete_track', language)}</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTrack?.episodes || deleteTrack?.id}</strong>
              <br />
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={permanent}
                  onChange={(e) => setPermanent(e.target.checked)}
                />
                <span>{t('admin_audio_permanent_delete', language)}</span>
              </label>
              {!permanent && (
                <span className="block mt-1 text-xs text-muted-foreground">
                  {t('admin_audio_soft_delete_hint', language)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel', language)}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {permanent ? t('admin_audio_delete_permanent', language) : t('admin_audio_deactivate', language)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminAudioLibraryPage;
